import { Observable, of, defer, EMPTY, timer, throwError } from "rxjs"
import {
    switchMap,
    map,
    catchError,
    filter,
    debounceTime,
    delay,
    buffer,
    mergeMap,
    retry,
    takeUntil,
} from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { Forbidden, Unauthorized } from "@figurepos/fgr-lib-business-logic/lib/http/errors"
import {
    LOAD_ACCOUNTS_SUCCESS,
    LOAD_AUTH_LOCATIONS_SUCCESS,
    LOAD_ORGANIZATIONS_SUCCESS,
    loadAccountsFailure,
    loadAccountsSuccess,
    loadAuthLocationsFailure,
    loadAuthLocationsSuccess,
    loadOrganizationsSuccess,
    LOG_OUT_SUCCESS,
    logOutSuccess,
} from "../layouts/header/actions"
import type { EpicDependencies } from "../rxjs/configureEpics"
import {
    ACCOUNT_SELECT_REQUESTED,
    ACCOUNT_SELECTED,
    accountSelected,
    accountSelectRequested,
    AUTH_LOCATIONS_REFRESH_REQUESTED,
    authLocationsRefreshed,
    COOKIE_LOADED,
    cookieLoaded,
    ORGANIZATION_SELECTED,
    REDIRECT_TO_APP,
    redirectToApp,
} from "./actions"
import { PAGE_LOADED } from "../layouts/actions"
import {
    areLocationsAuthDataLoaded,
    getAuthUserEmail,
    getAuthUserId,
    getHttpHeaders,
    getLocationsAuthData,
    getSelectedAccountId,
    getSelectedOrganizationId,
    getSession,
    getUserRole,
    hasMasterListAccess,
} from "./selectors"
import { SIGN_IN_SUCCESS } from "../screens/login/actions"
import type { LocationAuthData, LocationAuthResponse } from "./types"
import { AuthAccountsAndOrganizationsResponse, fetchAuthAccountsAndOrganizations, fetchAuthLocations } from "./request"
import { locationInitialized, locationOverrideInitialized } from "../common/locationsPicker/actions"
import { getSelectedLocationId, getSelectedLocationOverrideId } from "../common/locationsPicker/selectors"
import {
    getCookieAccountId,
    getCookieLocationId,
    getCookieLocationOverrideId,
    getCookieOrganizationId,
    getCookieSession,
    getCookieUserEmail,
    getCookieUserId,
    getCookieUserRole,
} from "./cookies"
import { cookieNames, deleteCookie, setCookie } from "../../utils/cookies"
import { AutoCloseMessageTypeEnum } from "../common/components/AutoCloseMessage/AutoCloseMessageComponent"
import { autoCloseMessageToggle, createLoadingEnded } from "../filters/actions"
import { AuthAccount2 } from "../../graphql/types"
import { Router } from "../../server/router"
import { transformAuthOrganizationData } from "./utils"
import { RELOAD_AUTH_ACCOUNTS_DATA } from "../screens/organization/actions"
import { logger } from "../../utils/loggerWrap"
import { Action, StateObject } from "../redux/types"

export const loadCookiesEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PAGE_LOADED),
        switchMap(() => {
            const session = getCookieSession()
            const userRole = getCookieUserRole()
            const accountId = getCookieAccountId()
            const locationId = getCookieLocationId()
            const locationOverrideId = getCookieLocationOverrideId()
            const userId = getCookieUserId()
            const userEmail = getCookieUserEmail()
            const organizationId = getCookieOrganizationId()

            // Account/location/organization selection is per-tab UI state, but the cookies backing it
            // are shared across all tabs. Only adopt the cookie value here when this tab doesn't already
            // have one selected (first hydration) - otherwise another tab switching accounts would
            // silently hijack this tab's selection (and its outgoing request headers) on the next navigation.
            const currentAccountId = getSelectedAccountId(deps.getState())
            const currentLocationId = getSelectedLocationId(deps.getState())
            const currentLocationOverrideId = getSelectedLocationOverrideId(deps.getState())
            const currentOrganizationId = getSelectedOrganizationId(deps.getState())

            const resolvedAccountId = currentAccountId || accountId
            const resolvedLocationId = currentLocationId || locationId
            const resolvedLocationOverrideId = currentLocationOverrideId || locationOverrideId
            const resolvedOrganizationId = currentOrganizationId || organizationId

            if (
                session !== getSession(deps.getState()) ||
                userRole !== getUserRole(deps.getState()) ||
                userId !== getAuthUserId(deps.getState()) ||
                resolvedAccountId !== currentAccountId ||
                resolvedLocationId !== currentLocationId ||
                resolvedLocationOverrideId !== currentLocationOverrideId ||
                resolvedOrganizationId !== currentOrganizationId ||
                userEmail !== getAuthUserEmail(deps.getState())
            ) {
                logger.info("loadCookiesEpic", {
                    session: session,
                    accountId: resolvedAccountId,
                    locationId: resolvedLocationId,
                    locationOverrideId: resolvedLocationOverrideId,
                    userRole: userRole,
                    userId: userId,
                    userEmail: userEmail,
                    organizationId: resolvedOrganizationId,
                })
                return [
                    cookieLoaded(
                        session,
                        resolvedAccountId,
                        resolvedLocationId,
                        resolvedLocationOverrideId,
                        userRole,
                        userId,
                        userEmail,
                        resolvedOrganizationId
                    ),
                ]
            } else {
                return EMPTY
            }
        })
    )

export const loadAuthAccountsEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(COOKIE_LOADED, SIGN_IN_SUCCESS, RELOAD_AUTH_ACCOUNTS_DATA),
        filter(() => !!getSession(deps.getState())),
        switchMap((action: Action) => {
            const userId = getAuthUserId(deps.getState()) ?? action.payload?.userId
            const session = action.payload?.session ?? getSession(deps.getState())

            logger.info("loadAuthAccountsEpic", { userId: userId, session: session })
            if (userId) {
                return defer(() => fetchAuthAccountsAndOrganizations(session, userId)).pipe(
                    switchMap((response: AuthAccountsAndOrganizationsResponse) => {
                        const organizationsData = transformAuthOrganizationData(
                            response.authOrganizations,
                            response.authAccounts2
                        )

                        const redirectToSelectAccountScreen =
                            action.type === SIGN_IN_SUCCESS && response.authOrganizations.length > 0

                        return [
                            ...(action.type === SIGN_IN_SUCCESS ? [redirectToApp(response)] : []),
                            loadAccountsSuccess(response.authAccounts2, redirectToSelectAccountScreen),
                            loadOrganizationsSuccess(organizationsData),
                        ]
                    }),
                    catchError(error => {
                        logger.error("loadAuthAccountsEpic", null, error)
                        return of(loadAccountsFailure(error))
                    })
                )
            }

            return []
        })
    )

export const loadAuthLocationsEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(COOKIE_LOADED, SIGN_IN_SUCCESS, ACCOUNT_SELECTED),
        filter(
            () =>
                !!getSession(deps.getState()) &&
                !!getSelectedAccountId(deps.getState()) &&
                areLocationsAuthDataLoaded(deps.getState()) === false
        ),
        switchMap(() => {
            logger.info("loadAuthLocationsEpic")
            return defer(() => fetchAuthLocations(getHttpHeaders(deps.getState()))).pipe(
                map((response: LocationAuthResponse) =>
                    loadAuthLocationsSuccess(response.locations, response.masterListAccess)
                ),
                catchError(error => of(loadAuthLocationsFailure(error)))
            )
        })
    )

const AUTH_LOCATIONS_REFRESH_DELAY_MS = 1000
const AUTH_LOCATIONS_REFRESH_ATTEMPTS = 5

const isRefreshedLocationReady = (
    response: LocationAuthResponse,
    locationId: string,
    knownLocations: LocationAuthData[]
): boolean =>
    response.locations.some(location => location.id === locationId && location.terminals.length > 0) &&
    knownLocations.every(known => response.locations.some(location => location.id === known.id))

export const refreshAuthLocationsEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AUTH_LOCATIONS_REFRESH_REQUESTED),
        mergeMap((action: Action) => {
            const locationId: string = action.payload.locationId
            const notReady = new Error(`Location ${locationId} is not ready yet`)
            return timer(AUTH_LOCATIONS_REFRESH_DELAY_MS).pipe(
                switchMap(() =>
                    defer(() => fetchAuthLocations(getHttpHeaders(deps.getState()))).pipe(
                        map((response: LocationAuthResponse) => {
                            if (
                                !isRefreshedLocationReady(response, locationId, getLocationsAuthData(deps.getState()))
                            ) {
                                throw notReady
                            }
                            return response
                        }),
                        retry({
                            count: AUTH_LOCATIONS_REFRESH_ATTEMPTS - 1,
                            delay: (error, retryCount) =>
                                error === notReady
                                    ? timer(AUTH_LOCATIONS_REFRESH_DELAY_MS * 2 ** (retryCount - 1))
                                    : throwError(() => error),
                        })
                    )
                ),
                map((response: LocationAuthResponse) =>
                    authLocationsRefreshed(response.locations, response.masterListAccess)
                ),
                catchError(error => {
                    logger.warn("refreshAuthLocationsEpic", { locationId: locationId }, error)
                    return EMPTY
                }),
                takeUntil(actions$.pipe(ofType(ACCOUNT_SELECTED, LOG_OUT_SUCCESS)))
            )
        })
    )

export const selectAccountEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOAD_ACCOUNTS_SUCCESS),
        switchMap((action: Action) => {
            logger.info("selectAccountEpic", {
                accounts: action.payload.accounts.map(a => a.id),
                redirectToSelectAccountScreen: action.payload.redirectToSelectAccountScreen,
            })

            // We don't want to select account yet if we are redirecting to 'select account/organization' agenda
            if (action.payload.redirectToSelectAccountScreen) {
                return []
            }

            const accounts: AuthAccount2[] = action.payload.accounts
            const selectedAccountId = getSelectedAccountId(deps.getState())

            if (!selectedAccountId && accounts && accounts.length > 0) {
                setCookie(cookieNames.accountId, accounts[0].id)
                return [accountSelectRequested(accounts[0].id)]
            } else if (selectedAccountId) {
                setCookie(cookieNames.accountId, selectedAccountId)
                return [accountSelectRequested(selectedAccountId)]
            }

            return []
        })
    )

export const selectAccountRequestedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECT_REQUESTED),
        switchMap((action: Action) => {
            logger.info("selectAccountRequestedEpic", { accountId: action.payload.accountId })
            const selectedAccountId = getSelectedAccountId(deps.getState())
            const accountId = action.payload.accountId as string
            if (selectedAccountId !== accountId) {
                return [accountSelected(accountId)]
            }
            return []
        })
    )

export const deleteOrganizationCookieEpic = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(LOAD_ORGANIZATIONS_SUCCESS),
        switchMap((action: Action) => {
            logger.info("deleteOrganizationCookieEpic", { organizations: action.payload.organizations.map(o => o.id) })
            if (action.payload.organizations.length === 0) {
                deleteCookie(cookieNames.organizationId)
            }

            return []
        })
    )

export const selectFirstLocationForPicker = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOAD_AUTH_LOCATIONS_SUCCESS),
        filter((action: Action) => action.payload.locations && action.payload.locations.length > 0),
        switchMap(action => {
            logger.info("selectFirstLocationForPicker", {
                locationIds: action.payload.locations.map(l => l.id),
                selectedLocationId: getSelectedLocationId(deps.getState()),
                hasMasterListAccess: hasMasterListAccess(deps.getState()),
            })

            const locations = action.payload.locations
            const selectedLocationId = getSelectedLocationId(deps.getState())
            const locationExists = locations.some(l => l.id === selectedLocationId)
            if (locationExists) {
                return []
            }

            setCookie(cookieNames.locationId, action.payload.locations[0].id)
            if (!hasMasterListAccess(deps.getState())) {
                setCookie(cookieNames.overrideLocationId, action.payload.locations[0].id)
            } else {
                deleteCookie(cookieNames.overrideLocationId)
            }
            return [
                locationInitialized(action.payload.locations[0].id),
                locationOverrideInitialized(
                    hasMasterListAccess(deps.getState()) ? null : action.payload.locations[0].id
                ),
            ]
        })
    )

export const logoutEpic = (actions$: Observable<Action>) =>
    actions$.pipe(
        switchMap((action: Action) => {
            const err = action.payload && action.payload.error

            if (!err) {
                return []
            }

            if (err instanceof Forbidden || err instanceof Unauthorized) {
                return [logOutSuccess(action.payload.message || "You've Been Logged Out Due To Session Expiration")]
            }

            if (err.status && (err.status === 401 || err.status === 403)) {
                return [logOutSuccess(action.payload.message || "You've Been Logged Out Due To Session Expiration")]
            }

            return []
        })
    )

export const logoutSuccessShowMessageEpic = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(LOG_OUT_SUCCESS),
        filter((action: Action) => action.payload.message),
        buffer(actions$.pipe(ofType(LOG_OUT_SUCCESS), debounceTime(1000))),
        switchMap((batch: Record<string, any>[]) => {
            const action = batch[0]

            if (action && action.payload.message) {
                return [
                    autoCloseMessageToggle({
                        text: action.payload.message,
                        type: AutoCloseMessageTypeEnum.Warning,
                    }),
                ]
            }

            return []
        })
    )

export const redirectToAppEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(REDIRECT_TO_APP),
        switchMap((action: Action) => {
            const response: AuthAccountsAndOrganizationsResponse = action.payload.authResponse

            if (response.authOrganizations.length > 0) {
                Router.push("/select-account")
            } else {
                Router.push("/")
            }
            return [createLoadingEnded("login-form")]
        })
    )

export const organizationSelectedEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(ORGANIZATION_SELECTED),
        switchMap((action: Action) => {
            logger.info("organizationSelectedEpics", { organizationId: action.payload.id })
            setCookie(cookieNames.organizationId, action.payload.id)

            return []
        })
    )

export const logoutSuccessClearMessageEpic = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(LOG_OUT_SUCCESS),
        delay(2000),
        map(() => autoCloseMessageToggle(null))
    )

export default [
    loadCookiesEpic,
    loadAuthAccountsEpic,
    loadAuthLocationsEpic,
    refreshAuthLocationsEpic,
    selectAccountEpic,
    selectFirstLocationForPicker,
    logoutEpic,
    logoutSuccessShowMessageEpic,
    logoutSuccessClearMessageEpic,
    redirectToAppEpics,
    organizationSelectedEpics,
    deleteOrganizationCookieEpic,
    selectAccountRequestedEpic,
]
